import { useSWRConfig } from 'swr';
import { putFetcher } from '@/api/fetchers';
import type { UpdateClient } from '@/schemas/API/PUT/client/request';
import type { ClientDetailed } from '@/schemas/API/GET/client/response';
import { clientDetailedMergeUpdateClient } from '@/schemas/converters/clientSchemasConverter';

/**
 * Hook that updates a client with the specified ID using the provided data.
 */
const useUpdateClient = () => {
  const { mutate } = useSWRConfig();

  /**
   * Updates a client with the specified ID using the provided data.
   * @param clientId The ID of the client to update.
   * @param updateClient The updated client data.
   * @returns A promise that resolves to the updated client details, or undefined if the update fails.
   */
  const updateClient = async (
    clientId: number,
    updateClient: UpdateClient
  ): Promise<ClientDetailed | undefined> => {
    const updateFn = async (): Promise<ClientDetailed> => {
      for (const contact of updateClient.contacts) {
        if (contact.title === '') {
          contact.title = null;
        }
      }

      // TODO It's a hotfix! Fix the updateClient schema
      return await putFetcher(`/clients/${clientId}`, {
        ...updateClient,
        active: 'true',
      });
    };

    const updatedClient = await mutate(`/clients/${clientId}`, updateFn, {
      optimisticData: oldClientDetailed =>
        clientDetailedMergeUpdateClient(oldClientDetailed, updateClient),
      revalidate: false,
      rollbackOnError: true,
      populateCache: true,
    });

    return updatedClient;
  };

  return {
    updateClient,
  };
};

export default useUpdateClient;
